import { Client, Protocol } from '@tencent/mega-ipc-renderer';
import {
    DocType,
    DocxChunkLoadEvent,
    DocxSaveEvent,
    DocxServiceProtocol,
    ImporterErrorCode,
    DocxInfo,
} from '@tencent/mega-sdk-protocols';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import {
    DataConvertError,
    ILocalEditDataConverter,
    LoadConvertHandlers,
    LoadPresentationOptions,
    PasswordError,
    SaveConvertHandlers,
    SavePresentationOptions,
} from '../core/interfaces/local-edit-data-converter.interface';
import { ICommandData, SlideFile } from '@tencent/slide-sdk-model';
import { ISlideInitialData, SlideInitialDataType } from '../../../core/interfaces/slide-initial-data-service.interface';
import { ChunkDataManager } from '../../../core/managers/chunk-data-manager';
import { reaction } from 'mobx';
import { IDocVerifierManager } from '@src/slide/domain/slide-app/core/interfaces/doc-verifier-manager.interface';
import { DocxSaveOptions } from '@tencent/mega-sdk-protocols/typings/mega-docx-service.protocol';

interface IHeadChunkData {
    headData: any;
    totalChunkCount: number;
}

export class DocxLocalEditDataConverter implements ILocalEditDataConverter {
    public readonly name: string = 'docx-chunks';

    protected requiredDocxServiceVersion = 2;

    protected client = Client.create({ docx: Protocol<DocxServiceProtocol>() }, ENV.IS_MOBILE_LOCAL_EDIT);

    public constructor(private docVerifierManager: IDocVerifierManager) {
    }

    public async loadPresentation(
        options: LoadPresentationOptions,
        handlers: LoadConvertHandlers = {},
    ): Promise<ISlideInitialData> {
        this.checkDocxService(handlers.onFatalError);
        const channel = await this.client.docx
            .loadByChunks({
                ...options,
                docType: DocType.Slide,
            })
            .catch((err: Error) => {
                handlers.onFatalError?.(err);
                throw err;
            });

        const continueWithPassword = (password: string) => {
            channel.publish(DocxChunkLoadEvent.ContinueWithPassword, password);
        };

        // 通知桌面端消息
        const publishDocxInfo = (info: DocxInfo) => {
            channel.publish(DocxChunkLoadEvent.Info, info);
        };

        const chunkDataManager = new ChunkDataManager<{ commands: ICommandData[] }>();
        const chunksGenerator = chunkDataManager.toChunksGenerator().advance();

        // 页面初始化请求首屏缓存数据 & 分页加载数据首屏数据 使用返回较早的
        const firstChunkReadyRace = (): Promise<IHeadChunkData> => Promise.race([
            new Promise<IHeadChunkData>((resolve) => {
                function resolveHeadChunkData() {
                    if ((window as any).localHeadChunkData?.data) {
                        const { headData, totalChunkCount } = (window as any).localHeadChunkData.data;
                        resolve({
                            headData,
                            totalChunkCount,
                        });
                    }
                }
                resolveHeadChunkData();
                window.addEventListener('localHeadChunkDataReady', () => {
                    resolveHeadChunkData();
                });
            }),
            new Promise<IHeadChunkData>((resolve) => {
                channel.subscribe(DocxChunkLoadEvent.Head, (headData, totalChunkCount) => {
                    resolve({
                        headData,
                        totalChunkCount,
                    });
                });
            })]);

        return new Promise((resolve, reject) => {
            const dispose = (err?: Error) => {
                chunkDataManager.dispose(err);
                channel.close();

                if (err) {
                    handlers.onFatalError?.(err);
                    reject(err);
                }
            };

            firstChunkReadyRace().then(async ({ headData, totalChunkCount }) => {
                const parseHeadData = this.parseChunkData(headData);

                // 检查是否存在编辑密码，是则弹框校验密码，通过后才继续后续流程
                await this.docVerifierManager.checkModifyVerifier(parseHeadData, publishDocxInfo);

                handlers.onHead?.(totalChunkCount);
                resolve({
                    type: SlideInitialDataType.CommandChunkData,
                    data: {
                        head: parseHeadData,
                        chunks: chunksGenerator.init(totalChunkCount),
                    },
                });
            });

            channel.subscribe(DocxChunkLoadEvent.Chunk, (chunkData, chunkIndex, totalChunkCount) => {
                chunkDataManager.add(chunkIndex, { commands: [this.parseChunkData(chunkData)] });
                handlers.onChunk?.(chunkIndex, totalChunkCount);
            });

            reaction(
                () => chunksGenerator.done,
                (done) => {
                    if (done) {
                        handlers.onEnd?.(chunksGenerator.total);
                        dispose();
                    }
                },
                {
                    fireImmediately: true,
                },
            );

            channel.subscribe(DocxChunkLoadEvent.Error, (err) => {
                const passwordErr = this.getPasswordError(err.code);
                if (passwordErr) {
                    // 检查是否存在打开密码，是则弹框校验密码，通过后才继续后续流程
                    this.docVerifierManager.checkOpenVerifier(
                        passwordErr,
                        err.code as number,
                        continueWithPassword,
                        publishDocxInfo,
                    );
                    return;
                }

                dispose(err);
            });
        });
    }

    public async savePresentation(options: SavePresentationOptions, handlers: SaveConvertHandlers = {}): Promise<void> {
        this.checkDocxService(handlers.onFatalError);
        const saveOptions: DocxSaveOptions = {
            ...options, docType: DocType.Slide, bypassUpdateIsModifiedWhenJSONSaved: true,
        };
        if (this.docVerifierManager.getOpenPassword()) {
            saveOptions.password = this.docVerifierManager.getOpenPassword();
        }
        const channel = await this.client.docx
            .save(saveOptions, options.saveType)
            .catch((err: Error) => {
                handlers.onFatalError?.(err);
                throw err;
            });

        channel.subscribe(DocxSaveEvent.DocumentSaved, (newPath, newTitle, newGlobalPadId) => {
            handlers.onDocumentSaved?.(newPath, newTitle, newGlobalPadId);
            channel.close();
        });

        channel.subscribe(DocxSaveEvent.Cancel, () => {
            handlers.onCancel?.();
            channel.close();
        });

        channel.subscribe(DocxSaveEvent.Abort, () => {
            handlers.onAbort?.();
            channel.close();
        });

        channel.subscribe(DocxSaveEvent.MobileError, (code: number, message: string) => {
            handlers.onFatalError?.({
                name: 'MobileError',
                code,
                message,
            });
            channel.close();
        });

        channel.subscribe(DocxSaveEvent.DesktopError, (code: number, message: string) => {
            handlers.onFatalError?.({
                name: 'DesktopError',
                code,
                message,
            });
            channel.close();
        });

        channel.subscribe(DocxSaveEvent.ConverterError, (code: number, message: string) => {
            handlers.onFatalError?.({
                name: 'ConverterError',
                code,
                message,
            });
            channel.close();
        });
    }

    protected parseChunkData(data: string | Uint8Array): ICommandData {
        if (data instanceof Uint8Array) {
            return SlideFile.fromEGChunkDataBuffer(data).getCommandData();
        }
        return new SlideFile(JSON.parse(data)).getCommandData();
    }

    protected checkDocxService(handleError?: (err: DataConvertError) => void) {
        if (this.client.docx.version < this.requiredDocxServiceVersion) {
            const err = new Error(`DocxService is not found or version is too low, expected version = ${this.requiredDocxServiceVersion}, actual version = ${this.client.docx.version}`);
            handleError?.(err);
            throw err;
        }
    }

    protected getPasswordError(code?: number) {
        switch (code) {
            case ImporterErrorCode.kPasswordRequired:
                return PasswordError.Required;
            case ImporterErrorCode.kInvalidPassword:
                return PasswordError.Invalid;
            default:
                return null;
        }
    }
}
